import { ElementType, ReactNode, forwardRef, KeyboardEvent, MouseEvent } from 'react';
import { css, cx } from '@linaria/atomic';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { KeyNames } from '../../../../constants/key-names';
import { StylesProp, splitStylesProp } from '../../../../helpers/styles-prop';
import type { PolymorphicComponentPropWithRef, PolymorphicRef } from '../../../../type-utils/polymorphic';
import { ContextMenu, ContextMenuActions } from '../../../context-menu/context-menu';
import { SkeletonLoader } from '../../../loader/skeleton-loader/skeleton-loader';
import { SpinningLoader } from '../../../loader/spinning-loader';
import { Text } from '../../../text/text.component';
import { useChatBubbleContext } from '../../chat';
import { ChatStyles } from '../../chat.styles';
import { AiActivityBubble } from './ai-activity-bubble';
import { CallBubble } from './call-bubble';
import { ImageBubble } from './image-bubble';
import { PdfBubble } from './pdf-bubble';
import { TextBubble } from './text-bubble';
import { VideoBubble } from './video-bubble';

const IMAGE_SKELETON_SIZE = {
  width: 'min(70vw, 320px)',
  height: 'min(40vh, 480px)',
} as const;

const sendingStyle = css`
  opacity: 0.6;
`;

const errorContainerStyle = css`
  padding: ${theme.spacing(2)};
`;

export type BubbleProps = {
  text?: string | ReactNode;
  subText?: ReactNode;
  bubbleIcon?: ReactNode;
  onClick?: (e?: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>) => void;
  contextActions?: ContextMenuActions;
  contextMenuReturnFocus?: boolean;
  isDeleted?: boolean;
  styles?: StylesProp;
  variant: 'text' | 'deleted' | 'image' | 'call' | 'ai-activity' | 'pdf' | 'video';
  src: string;
  maxWidth: string;
  maxHeight: string;
  pdfProps?: {
    filename?: string;
    fileSizeBytes?: number;
    onClickPreview?: () => void;
    previewTrackingId?: string;
  };
  videoProps?: {
    onClickPreview?: () => void;
    previewTrackingId?: string;
  };
  imageProps?: {
    suppressPreview?: boolean;
    onLoad?: () => void;
    onError?: (event: React.SyntheticEvent<HTMLImageElement, Event>) => void;
    onClickDownload?: () => void;
    downloadImageTrackingId?: string;
    backgroundColor?: (typeof theme.colors)[keyof typeof theme.colors];
    className?: string;
  };
  imageLoadError?: boolean;
  callProps?: {
    callResult?: 'missed' | 'inbound' | 'outbound' | 'abandoned' | 'voicemail';
    callAction: () => void;
    playAction?: () => void;
  };
  leadingIcon?: ReactNode;
};

type PolymorphicBubbleProps<C extends ElementType = 'li'> = PolymorphicComponentPropWithRef<C, BubbleProps>;

const BubbleComponent = <C extends ElementType = 'li'>(
  {
    as,
    text,
    subText,
    children,
    bubbleIcon,
    onClick,
    contextActions,
    contextMenuReturnFocus,
    variant = 'text',
    src,
    imageProps,
    imageLoadError,
    pdfProps,
    videoProps,
    backgroundColor: backgroundColorProp,
    styles,
    maxWidth,
    callProps,
    maxHeight,
    leadingIcon,
  }: PolymorphicBubbleProps<C>,
  ref: PolymorphicRef<C>
) => {
  const Component = as || 'li';
  const { direction, isSending } = useChatBubbleContext();
  const bubbleStyles = cx(
    ChatStyles.bubble(),
    direction === 'inbound' ? ChatStyles.bubbleInbound : ChatStyles.bubbleOutbound
  );

  const isImageOrVideo = variant === 'image' || variant === 'video';
  const paddingVal = isImageOrVideo ? 0 : variant === 'pdf' ? theme.spacing(1) : theme.spacing(2);
  const displayVal = isImageOrVideo ? 'list-item' : 'flex';
  const overflowVal = isImageOrVideo || variant === 'pdf' ? 'hidden' : 'visible';
  const bgColorVal = backgroundColorProp ?? (direction === 'inbound' ? theme.colors.neutral5 : theme.colors.primary5);
  const maxHeightVal = maxHeight ?? (isImageOrVideo ? 'min(40vh, 480px)' : '100%');
  const maxWidthVal = maxWidth ?? (isImageOrVideo ? 'min(40%, 320px)' : variant === 'pdf' ? 'min(80%, 320px)' : '100%');

  const styleObj = css`
    padding: var(--padding);
    display: var(--display);
    overflow: var(--overflow);
    background-color: var(--bg-color);
    max-height: var(--max-height);
    max-width: var(--max-width);
  `;

  const styleVars = {
    '--padding': paddingVal,
    '--display': displayVal,
    '--overflow': overflowVal,
    '--bg-color': bgColorVal,
    '--max-height': maxHeightVal,
    '--max-width': maxWidthVal,
  } as React.CSSProperties;

  const { css: customCss, className: customClassName } = splitStylesProp(styles);

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === KeyNames.Enter || event.key === KeyNames.Space) {
      onClick?.(event);
    }
  };

  if (isSending || (variant === 'image' && !src)) {
    return (
      <Component
        ref={ref}
        className={cx(styleObj, bubbleStyles, isSending && sendingStyle, customClassName)}
        style={styleVars}
        {...(customCss !== undefined ? { css: customCss } : undefined)}
      >
        {variant === 'image' ? (
          imageLoadError && !isSending ? (
            <div className={errorContainerStyle}>
              <Icon name='warning-badge' color='error' />
              <Text data-testid='chat-image-error-message'>There was an error loading this image.</Text>
            </div>
          ) : (
            <SkeletonLoader
              width={IMAGE_SKELETON_SIZE.width}
              height={IMAGE_SKELETON_SIZE.height}
              animation='shimmer'
              data-testid='chat-image-skeleton-loader'
            />
          )
        ) : !text ? (
          <SpinningLoader size='medium' />
        ) : (
          <TextBubble text={text} subText={subText} variant='text' />
        )}
      </Component>
    );
  }

  if (!contextActions?.length) {
    return (
      <Component
        ref={ref}
        onClick={onClick}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        className={cx(styleObj, bubbleStyles, customClassName)}
        style={styleVars}
        {...(customCss !== undefined ? { css: customCss } : undefined)}
      >
        {bubbleIcon}
        {variant === 'image' ? (
          <ImageBubble src={src} {...imageProps} />
        ) : variant === 'pdf' ? (
          <PdfBubble src={src} {...pdfProps} />
        ) : variant === 'video' ? (
          <VideoBubble src={src} {...videoProps} />
        ) : variant === 'call' ? (
          <CallBubble
            title={text}
            subtitle={subText}
            callResult={callProps?.callResult}
            callAction={callProps?.callAction}
            playAction={callProps?.playAction}
          >
            {children}
          </CallBubble>
        ) : variant === 'ai-activity' ? (
          <AiActivityBubble text={text} leadingIcon={leadingIcon} />
        ) : (
          <TextBubble text={text} subText={subText} variant={variant} />
        )}
      </Component>
    );
  }

  return (
    <ContextMenu actions={contextActions ?? []} returnFocus={contextMenuReturnFocus}>
      <Component
        ref={ref}
        onClick={onClick}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        className={cx(styleObj, bubbleStyles, customClassName)}
        style={styleVars}
        {...(customCss !== undefined ? { css: customCss } : undefined)}
      >
        {bubbleIcon}
        {variant === 'image' ? (
          <ImageBubble src={src} {...imageProps} />
        ) : variant === 'pdf' ? (
          <PdfBubble src={src} {...pdfProps} />
        ) : variant === 'video' ? (
          <VideoBubble src={src} {...videoProps} />
        ) : variant === 'call' ? (
          <CallBubble
            title={text}
            subtitle={subText}
            callResult={callProps?.callResult}
            callAction={callProps?.callAction}
            playAction={callProps?.playAction}
          >
            {children}
          </CallBubble>
        ) : variant === 'ai-activity' ? (
          <AiActivityBubble text={text} leadingIcon={leadingIcon} />
        ) : (
          <TextBubble text={text} subText={subText} variant={variant} />
        )}
      </Component>
    </ContextMenu>
  );
};

export const Bubble = forwardRef(BubbleComponent);
Bubble.displayName = 'Bubble';
